/**
 * Acesso compartilhado ao IndexedDB da fila (v2: active + shadow + metadata).
 */

import type { QueueItem } from '../queue/types';

export const DB_NAME = 'gomarke_pixel_queue_v2';
export const DB_VERSION = 2;
export const STORE_ITEMS = 'queue_items';
export const STORE_SHADOW = 'queue_items_shadow';
export const STORE_META = 'metadata';
export const STORE_REPLAY = 'replay_cursors';

export interface StorageMetaRecord {
  key: string;
  activeGeneration: number;
  itemCount: number;
  checksum: string;
  updatedAt: number;
}

let dbPromise: Promise<IDBDatabase | null> | null = null;

export function openQueueDb(): Promise<IDBDatabase | null> {
  if (typeof indexedDB === 'undefined') return Promise.resolve(null);
  if (!dbPromise) {
    dbPromise = new Promise(resolve => {
      try {
        const req = indexedDB.open(DB_NAME, DB_VERSION);
        req.onerror = () => resolve(null);
        req.onupgradeneeded = () => {
          const db = req.result;
          if (!db.objectStoreNames.contains(STORE_ITEMS)) {
            const store = db.createObjectStore(STORE_ITEMS, { keyPath: 'id' });
            store.createIndex('by_status', 'status', { unique: false });
            store.createIndex('by_session_sequence', 'sessionSequence', {
              unique: false,
            });
          }
          if (!db.objectStoreNames.contains(STORE_SHADOW)) {
            db.createObjectStore(STORE_SHADOW, { keyPath: 'id' });
          }
          if (!db.objectStoreNames.contains(STORE_META)) {
            db.createObjectStore(STORE_META, { keyPath: 'key' });
          }
          if (!db.objectStoreNames.contains(STORE_REPLAY)) {
            db.createObjectStore(STORE_REPLAY, { keyPath: 'sessionId' });
          }
        };
        req.onsuccess = () => resolve(req.result);
      } catch {
        resolve(null);
      }
    });
  }
  return dbPromise;
}

export function _testResetQueueDb(): void {
  dbPromise = null;
}

export async function idbGetAll(
  storeName: string
): Promise<QueueItem[]> {
  const db = await openQueueDb();
  if (!db) return [];
  return new Promise(resolve => {
    try {
      const tx = db.transaction(storeName, 'readonly');
      const req = tx.objectStore(storeName).getAll();
      req.onsuccess = () => resolve((req.result as QueueItem[]) ?? []);
      req.onerror = () => resolve([]);
    } catch {
      resolve([]);
    }
  });
}

export async function idbPut(
  storeName: string,
  item: QueueItem
): Promise<void> {
  const db = await openQueueDb();
  if (!db) return;
  return new Promise((resolve, reject) => {
    try {
      const tx = db.transaction(storeName, 'readwrite');
      tx.objectStore(storeName).put(item);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    } catch {
      resolve();
    }
  });
}

export async function idbDelete(storeName: string, id: string): Promise<void> {
  const db = await openQueueDb();
  if (!db) return;
  return new Promise((resolve, reject) => {
    try {
      const tx = db.transaction(storeName, 'readwrite');
      tx.objectStore(storeName).delete(id);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    } catch {
      resolve();
    }
  });
}

export async function idbClearStore(storeName: string): Promise<void> {
  const db = await openQueueDb();
  if (!db) return;
  return new Promise((resolve, reject) => {
    try {
      const tx = db.transaction(storeName, 'readwrite');
      tx.objectStore(storeName).clear();
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    } catch {
      resolve();
    }
  });
}

export async function idbPutMeta(record: StorageMetaRecord): Promise<void> {
  const db = await openQueueDb();
  if (!db) return;
  return new Promise((resolve, reject) => {
    try {
      const tx = db.transaction(STORE_META, 'readwrite');
      tx.objectStore(STORE_META).put(record);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    } catch {
      resolve();
    }
  });
}

export async function idbGetMeta(): Promise<StorageMetaRecord | null> {
  const db = await openQueueDb();
  if (!db) return null;
  return new Promise(resolve => {
    try {
      const tx = db.transaction(STORE_META, 'readonly');
      const req = tx.objectStore(STORE_META).get('queue_meta');
      req.onsuccess = () => resolve((req.result as StorageMetaRecord) ?? null);
      req.onerror = () => resolve(null);
    } catch {
      resolve(null);
    }
  });
}
